import { MessageSquare, Zap } from 'lucide-react';

// TypeScript interfaces for pricing data
export interface Feature {
  id: string;
  displayName: string;
  category: string;
  note?: boolean;
  noteOnly?: boolean;
}

export interface TierFeatures {
  [key: string]: {
    included: boolean;
    value?: string;
    noteOnly?: boolean;
  };
}

export interface PricingTier {
  name: string;
  price: string;
  period: string;
  description: string;
  icon: any;
  buttonText: string;
  buttonVariant: string;
  highlight: boolean;
  features: TierFeatures;
}

// Master list of all features - ordered to match homepage section order:
// Case Briefs → Study → Issue Spotter → Essay Coach → Intelligence → Notes → Outlines → Chat → Courses
export const masterFeatures: Feature[] = [
  { id: 'case_briefs', displayName: 'Case briefs', category: 'Core Features' },
  { id: 'flashcards', displayName: 'Flashcards', category: 'Study Tools' },
  { id: 'question_bank', displayName: 'Practice questions', category: 'Study Tools' },
  { id: 'issue_spotter', displayName: 'Issue Spotter', category: 'Practice Tools' },
  { id: 'essay_coach', displayName: 'Essay Coach', category: 'Practice Tools' },
  { id: 'outline_iq', displayName: 'Outline feedback', category: 'Study Tools' },
  { id: 'notes', displayName: 'Notes & templates', category: 'Study Tools' },
  { id: 'topic_outlines', displayName: 'Topic outlines', category: 'Core Features' },
  { id: 'chat_messages', displayName: 'AI chat', category: 'Core Features' },
  { id: 'video_courses', displayName: 'Video courses', category: 'Courses' },
];

// Pricing tiers configuration
export const pricingTiers: PricingTier[] = [
  {
    name: 'Free',
    price: '$0',
    period: 'month',
    description: 'Unlimited access for 7 days.',
    icon: MessageSquare,
    buttonText: 'Current Plan',
    buttonVariant: 'outline',
    highlight: false,
    features: {
      chat_messages: { included: true, value: '10 per month' },
      case_briefs: { included: true },
      topic_outlines: { included: true, value: 'Limited' },
      notes: { included: false },
      outline_iq: { included: false },
      flashcards: { included: true, value: 'Limited' },
      question_bank: { included: false },
      issue_spotter: { included: false },
      essay_coach: { included: false },
      video_courses: { included: false },
    },
  },
  {
    name: 'Unlimited',
    price: '$20',
    period: 'month',
    description: 'Full access to all features.',
    icon: Zap,
    buttonText: 'Upgrade to Unlimited',
    buttonVariant: 'default',
    highlight: true,
    features: {
      chat_messages: { included: true, value: 'Unlimited' },
      case_briefs: { included: true },
      topic_outlines: { included: true, value: 'Full Access' },
      notes: { included: true },
      outline_iq: { included: true },
      flashcards: { included: true, value: 'Full Access' },
      question_bank: { included: true },
      issue_spotter: { included: true },
      essay_coach: { included: true },
      video_courses: { included: true },
    },
  },
];

// Helper functions
export const getTierFeatureInfo = (tier: PricingTier, featureId: string) => {
  return tier.features[featureId];
};

export const isFeatureIncluded = (tier: PricingTier, featureId: string): boolean => {
  return tier.features[featureId]?.included || false;
}; 